"use client";
import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { addDoc, collection } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebaseConfig";

// Importaciones de React Quill
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Autocomplete
import Autocomplete from "@mui/material/Autocomplete";

const storage = getStorage();

export default function CreateBlog() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [date, setDate] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Nuevo campo: categoría
  const [category, setCategory] = useState("");
  // Manejamos algunas categorías iniciales localmente:
  const [categories, setCategories] = useState(["Noticias", "Salud", "Tips"]);

  // Nuevo campo: lugar
  const [lugar, setLugar] = useState("");

  const handleImageUpload = async () => {
    if (!selectedImage) return null;
    const storageRef = ref(storage, `blogs/${selectedImage.name}`);
    await uploadBytes(storageRef, selectedImage);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const handleSubmit = async () => {
    if (!title || !content || !author || !date || !category || !lugar) {
      alert("Por favor, completa todos los campos (incluyendo categoría y lugar).");
      return;
    }

    setUploading(true);

    try {
      const imageUrl = await handleImageUpload();

      const blogData = {
        title,
        content, // React Quill maneja HTML
        author,
        date,
        image: imageUrl || "",
        comments: 0, // Inicia con 0 comentarios
        category,
        lugar,
      };

      await addDoc(collection(db, "blogs"), blogData);

      alert("¡Blog creado exitosamente!");
      // Limpiamos los campos
      setTitle("");
      setContent("");
      setAuthor("");
      setDate("");
      setCategory("");
      setLugar("");
      setSelectedImage(null);
      setPreviewImage("");
    } catch (error) {
      console.error("Error al crear el blog:", error);
      alert("Hubo un error al crear el blog.");
    } finally {
      setUploading(false);
      setDialogOpen(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setSelectedImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Configuración de React Quill
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "link",
  ];

  // Maneja la creación de nueva categoría si el usuario escribe algo no existente.
  const handleCategoryChange = (event, newValue) => {
    if (newValue && !categories.includes(newValue)) {
      // Agregar la nueva categoría al listado
      setCategories([...categories, newValue]);
    }
    setCategory(newValue);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ marginBottom: 4, textAlign: "center" }}>
        Crear Nuevo Blog
      </Typography>
      <Paper elevation={3} sx={{ padding: 4 }}>
        <TextField
          label="Título"
          variant="outlined"
          fullWidth
          sx={{ marginBottom: 2 }}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <TextField
          label="Autor"
          variant="outlined"
          fullWidth
          sx={{ marginBottom: 2 }}
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
        />

        <TextField
          type="date"
          label="Fecha"
          variant="outlined"
          fullWidth
          sx={{ marginBottom: 2 }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />

        {/* Campo "Categoría": Autocomplete con freeSolo */}
        <Autocomplete
          freeSolo
          options={categories}
          value={category}
          onChange={handleCategoryChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Categoría"
              variant="outlined"
              sx={{ marginBottom: 2 }}
            />
          )}
          sx={{ marginBottom: 2 }}
        />

        {/* Campo "Lugar": Select con 3 opciones */}
        <FormControl fullWidth sx={{ marginBottom: 2 }}>
          <InputLabel id="lugar-label">Lugar</InputLabel>
          <Select
            labelId="lugar-label"
            id="lugar-select"
            value={lugar}
            label="Lugar"
            onChange={(e) => setLugar(e.target.value)}
          >
            <MenuItem value={"cedeacc"}>Cedeacc</MenuItem>
            <MenuItem value={"clinica"}>Clínica</MenuItem>
            <MenuItem value={"centro"}>Centro</MenuItem>
          </Select>
        </FormControl>

        {/* Editor de texto enriquecido para 'content' */}
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          Contenido
        </Typography>
        <ReactQuill
          theme="snow"
          value={content}
          onChange={setContent}
          modules={quillModules}
          formats={quillFormats}
          style={{ marginBottom: 20 }}
        />

        <Button variant="contained" component="label" sx={{ marginBottom: 2 }}>
          Subir Imagen
          <input type="file" accept="image/*" hidden onChange={handleImageChange} />
        </Button>

        {previewImage && (
          <img
            src={previewImage}
            alt="Vista previa"
            style={{
              maxWidth: "100%",
              maxHeight: "300px",
              marginBottom: "20px",
              borderRadius: "8px",
            }}
          />
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={() => setDialogOpen(true)}
          disabled={uploading}
          fullWidth
        >
          {uploading ? "Subiendo..." : "Crear Blog"}
        </Button>
      </Paper>

      {/* Diálogo de confirmación */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirmar Creación</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ¿Estás seguro de que deseas crear este blog? Una vez creado, no se
            podrá editar directamente aquí.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
